import { useEffect, useState } from "react";
import useConfig from "../../demos/hooks/useConfig";
import { useButtonColors } from "../../hooks/useButtonColors";
import { useLocalStorage } from "../../utils/useLocalStorage";

interface BasicAuthFormProps {
  children: React.ReactNode;
}

const BasicAuthForm: React.FC<BasicAuthFormProps> = ({ children }) => {
  const { config } = useConfig();
  useButtonColors();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined
  );
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const { getItem, setItem } = useLocalStorage();

  useEffect(() => {
    // Check if authentication is enabled
    if (!config.enableBasicAuth) {
      setIsAuthenticated(true);
      return;
    }

    // Check if already authenticated
    const authStatus = getItem("basic_auth_status");
    // Todo uncomment this
    if (authStatus === "authenticated") {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [getItem]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      credentials.username === config.basicAuthUsername &&
      credentials.password === config.basicAuthPassword
    ) {
      setItem("basic_auth_status", "authenticated");
      setIsAuthenticated(true);
      setError(null);
    } else {
      setError("Invalid username or password");
    }
  };

  console.log("isAuthenticated", isAuthenticated);

  if (isAuthenticated === undefined) {
    return null;
  }

  // If not using basic auth or already authenticated, render children
  if (!config.enableBasicAuth || isAuthenticated) {
    return <>{children}</>;
  }

  // Otherwise, show login form
  return (
    <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Authentication Required
        </h2>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={credentials.username}
              onChange={handleInputChange}
            />
          </div>

          <div className="mb-6">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="w-full px-3 py-2 !border !border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={credentials.password}
              onChange={handleInputChange}
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default BasicAuthForm;
